"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.computePartTopology = computePartTopology;
var _findSelfIntersections = require("./findSelfIntersections");
var _findIntersections = require("./findIntersections");
var _getPointTangent = require("./getPointTangent");
var _entityHitTest = require("./entityHitTest");
var _vector = require("./vector");
var _BoundingBoxIntersector = require("./BoundingBoxIntersector");
/**
 * Detects topological information about the contours such as self-intersecting, intersecting another contour, inner or outer, parent contour, etc.
 * @param contours The array of contours
 * @param nonTopologicalContours An array of contour ids that should be ignored while calculating inner and outer contours
 * @returns An array of information, with one item for each contour, containing the topological information for that contour
 */
function computePartTopology(contours, nonTopologicalContours = []) {
  /* Properties mutated by this function:
   *  
   * selfIntersectingPoints write-once
   * selfIntersecting write-once
   * intersectingPoints write-once
   * intersecting write-once then read
   * type write-once (except for a sanity check)
   * insideOf write-once, read-multiple
   * parentId write-once
   * isOuterMost write-once
   * 
   * In order to get this to work in the client app, we won't be able to directly mutate these.
   * Instead, we'll return an array with one object for each contour which contains these properties.
   */

  const LOG_TIMES = false;
  let start;
  if (LOG_TIMES) {
    start = process.hrtime();
  }

  // This is to make it faster to get the contour a particular topo belongs to.
  let symContour = Symbol('contour');

  // Create successively expanding types as we go. Begin with just the id.
  const topo = contours.map(c => ({
    id: c.id,
    [symContour]: c
  }));

  // Build neighbor list
  // For each contour, the list of other contours whose bboxs intersect
  let neighbors = [];
  for (let i = 0; i < contours.length; i++) {
    if (!(0, _vector.isBboxDefined)(contours[i].bbox)) throw new Error(`Contour ${contours[i].id}'s bounding box is not fully defined`);
    neighbors[i] = [];
  }
  let intersector = new _BoundingBoxIntersector.BoundingBoxIntersector(contours, c => c.bbox);
  for (let intersection of intersector.getIntersections()) {
    neighbors[intersection[0].index].push(intersection[1].index);
    neighbors[intersection[1].index].push(intersection[0].index);
  }

  // Sort each neighbor list by index so that the order of contour ids in insideOf, childOf will be consistent with
  // previous implementations
  for (let i = 0; i < neighbors.length; i++) {
    neighbors[i].sort((a, b) => a - b);
  }
  if (LOG_TIMES) {
    console.log(`Computing neighbor list took ${getTimeInMs(process.hrtime(start))} ms`);
    start = process.hrtime();
  }

  // Detect self-intersecting contours
  for (let i = 0; i < contours.length; i++) {
    const c = contours[i];
    const t = topo[i];
    if (c.isClosed) {
      let intersects = (0, _findSelfIntersections.findSelfIntersections)(c).map(p => p.point);
      if (intersects.length > 0) {
        t.selfIntersectingPoints = intersects;
        t.selfIntersecting = true;
      } else {
        t.selfIntersecting = false;
      }
    }
  }
  if (LOG_TIMES) {
    console.log(`Computing self-intersections took ${getTimeInMs(process.hrtime(start))} ms`);
    start = process.hrtime();
  }
  for (let i = 0; i < topo.length; i++) {
    topo[i].intersecting = false;
  }

  // Detect intersections between contours
  for (let i = 0; i < contours.length - 1; i++) {
    const ci = contours[i];
    const ti = topo[i];
    if (!ci.isClosed || nonTopologicalContours.includes(ci.id)) {
      continue;
    }
    for (let j of neighbors[i]) {
      const cj = contours[j];
      const tj = topo[j];
      if (!cj.isClosed || nonTopologicalContours.includes(cj.id)) {
        continue;
      }
      let intersects = (0, _findIntersections.findIntersections)(ci, cj, false).map(p => p.point);
      if (intersects.length > 0) {
        ti.intersectingPoints = ti.intersectingPoints || [];
        ti.intersectingPoints.push(...intersects);
        ti.intersecting = true;
        tj.intersectingPoints = tj.intersectingPoints || [];
        tj.intersectingPoints.push(...intersects);
        tj.intersecting = true;
      }
    }
  }
  if (LOG_TIMES) {
    console.log(`Computing intersections took ${getTimeInMs(process.hrtime(start))} ms`);
    start = process.hrtime();
  }

  // Determine whether each closed contour is an inner or outer contour
  // We'd also like to know which parent an open contour is inside of, too, if possible
  for (let i = 0; i < contours.length; i++) {
    const c = contours[i];
    const t = topo[i];

    // If a contour is intersecting another contour other than itself, we can't determine if it is an inner or outer
    // contour. Nor should we classify it as inner or outer if it is self-intersecting.

    if (nonTopologicalContours.includes(c.id)) {
      t.type = 'nonTopological'; // Neither inner nor outer
    } else if (t.intersecting || t.selfIntersecting) {
      t.type = 'intersecting';
    } else if (!c.isClosed) {
      t.type = 'open';
    } else {
      // This contour is inner or outer, and we will determine that shortly
      t.type = 'inner-or-outer';
    }

    // Determine which contours this one is inside of

    // Choose a random point on this contour 
    // TODO: For open contours which might exist partially outside the parent, maybe we should choose several points
    let p = (0, _getPointTangent.getPointTangentOfEntity)(c.entities[Math.floor(Math.random() * c.entities.length)], Math.random())[0];
    t.insideOf = []; // id's of contours that surround this contour

    for (let neighj = 0; neighj < neighbors[i].length; neighj++) {
      let j = neighbors[i][neighj];
      let d = contours[j];
      let dTopo = topo[j];

      // Only test inner and outer contours
      if (dTopo.intersecting || dTopo.selfIntersecting) continue;
      if (!d.isClosed) continue;
      if (nonTopologicalContours.includes(d.id)) continue;

      // Determine whether the point on c is inside d
      let part = {
        arcified: {
          bbox: d.bbox,
          contours: [d]
        }
      };
      if ((0, _entityHitTest.isPointInsidePart)(p, part, true)) {
        t.insideOf.push(d.id);
      }
    }

    // Determine if this contour is inner or outer
    if (t.type === 'inner-or-outer') {
      if (t.insideOf.length % 2 === 0) {
        t.type = 'outer';
      } else {
        t.type = 'inner';
      }
    }
  }
  if (LOG_TIMES) {
    console.log(`Computing inner/outer contours took ${getTimeInMs(process.hrtime(start))} ms`);
    start = process.hrtime();
  }

  // Each TopologyInfo will definitely have type and insideOf properties now.
  const topo2 = topo;

  // Now, determine each contour's direct parent by examining the insideOf arrays.
  // Root nodes will have no items in the insideOf array.
  // Contours with exactly one item can be assigned that id as the parent.
  // Contours with two items--the item that has more items itself is the direct parent.
  // And so on.
  for (let h = 0; h < topo2.length; h++) {
    const t = topo2[h];
    if (t.type === 'outer' || t.type === 'inner') {
      // For each inner or outer contour, find the insideOf contour that has the most items it's inside of itself. That should be this contour's direct parent.
      if (t.insideOf.length > 0) {
        let parentId = -1;
        let mostItems = -1;
        for (let i = 0; i < t.insideOf.length; i++) {
          let parent = topo2.find(d => d.id === t.insideOf[i]); // parent = topo2[t.insideOf[i]] should also work but we shouldn't assume id's are sequential
          if (!parent || !parent.insideOf) {
            throw new Error(`Could not find parent ${t.insideOf[i]}`);
          }
          if (parent.insideOf.length > mostItems) {
            parentId = t.insideOf[i];
            mostItems = parent.insideOf.length;
          }
        }
        if (parentId === -1) {
          throw new Error('Could not determine parent contour');
        }
        t.parentId = parentId;
        t.isOuterMost = false;
      } else {
        t.parentId = -1;
        t.isOuterMost = true;
      }
    } else {
      // For each non inner or outer contour, find the insideOf *outer* contour that has the most items it's inside of itself. That should be this contour's direct parent.
      // console.log('Trying to assign parentId to a contour of type ' + t.type)
      if (t.insideOf.length > 0) {
        let parentId = -1;
        let mostItems = -1;
        for (let i = 0; i < t.insideOf.length; i++) {
          let parent = topo2.find(d => d.id === t.insideOf[i]); // parent = topo2[t.insideOf[i]] should also work but we shouldn't assume id's are sequential
          if (!parent || !parent.insideOf) {
            throw new Error(`Could not find parent ${t.insideOf[i]}`);
          }
          // Open contours can only be a child of an outer contour
          if (parent.insideOf.length > mostItems && parent.type === 'outer') {
            parentId = t.insideOf[i];
            mostItems = parent.insideOf.length;
          }
        }
        if (parentId === -1) {
          throw new Error('Could not determine parent contour of this non-inner/outer contour');
        }
        t.parentId = parentId;
      } else {
        t.parentId = -1;
      }
      t.isOuterMost = false;
    }
  }
  if (LOG_TIMES) {
    console.log(`Assigning parent contours took ${getTimeInMs(process.hrtime(start))} ms`);
    start = process.hrtime();
  }

  // To preserve backwards-compatibility, we'll introduce a new property for open contours--childOf--to indicate which
  // outer contour(s) the open contour is inside of. The test will be slightly different (and more accurate). We can use
  // childOf in new code, and continue using parentId and insideOf in old code. Then we can gradually deprecate parentId
  // and insideOf for open contours.

  // An open contour can only be a child of outer contours.
  // An open contour is a child of an outer contour if any point on that contour touches the space between the outer contour and its immediate child inner contours.

  // If an open contour intersects any outer contour, it must be partially "inside" that part, and automatically becomes a child of that contour. (Then there is no need to test that open contour's immediate child inner contours.)
  // If an open contour intersects any inner contour, it automatically becomes a child of the inner contour's parent.
  // If an open contour intersects no outer or inner contours at all, then it is a child of exactly zero or one outer contour. Choose a point on the open contour. Then for each outer contour, perform a isPointInsidePart test on it and its immediate child inner contours. Continue until the point is inside one of the parts, at which point it becomes a child of the outer contour, or until we have tested all outer contours, at which point it is a child of nothing.

  // Add parentId to the type.
  const topo3 = topo2;
  let outerTopos = topo3.filter(t => t.type === 'outer');
  let innerTopos = topo3.filter(t => t.type === 'inner');
  let openTopos = topo3.filter(t => t.type !== 'outer' && t.type !== 'inner');
  if (LOG_TIMES) {
    console.log(`There are ${outerTopos.length} outer contours`);
    console.log(`There are ${innerTopos.length} inner contours`);
    console.log(`There are ${openTopos.length} open contours`);
  }
  for (let openTopo of openTopos) {
    openTopo.childOf = [];
    const openContourIndex = contours.findIndex(c => c.id === openTopo.id);
    const openContour = contours[openContourIndex];

    // Search for intersections with this open contour's neighbors which are outer contours. If an open contour
    // intersects the boundary of an outer contour, it becomes a child of that outer contour.
    for (let neigh of neighbors[openContourIndex]) {
      // Skip if not an outer contour
      if (topo3[neigh].type !== 'outer') continue;
      let outerContour = contours[neigh];
      let outerTopo = topo3[neigh];
      let intersects = (0, _findIntersections.findIntersections)(openContour, outerContour, false);
      if (intersects.length > 0) {
        openTopo.childOf.push(outerTopo.id);
      }
    }

    // Search for intersections with this open contour's neighbors which are inner contours. If an open contour
    // intersects the boundary of an inner contour, it becomes a child of that inner contour's parent.
    for (let neigh of neighbors[openContourIndex]) {
      // Skip if not an inner contour
      if (topo3[neigh].type !== 'inner') continue;
      let innerContour = contours[neigh];
      let innerTopo = topo3[neigh];
      if (openTopo.childOf.includes(innerTopo.parentId)) {
        // Skip inner contours if the parentId is already in childOf
        continue;
      }
      let intersects = (0, _findIntersections.findIntersections)(openContour, innerContour, false);
      if (intersects.length > 0) {
        openTopo.childOf.push(innerTopo.parentId);
      }
    }

    // If no intersections have been found, perform isPointInPart tests against this open contour's neighbors which are
    // outer contours, and those outer contours' immediate children which are also neighbors of this contour. A positive
    // results means an open contour is inside of the "part" defined by the outer contour and its immediate child inner
    // contours which are near the open contour.
    if (openTopo.childOf.length === 0) {
      let pt = (0, _getPointTangent.getPointTangentOfEntity)(openContour.entities[Math.floor(Math.random() * openContour.entities.length)], Math.random())[0];
      for (let neigh of neighbors[openContourIndex]) {
        if (topo3[neigh].type !== 'outer') continue;
        let outerContour = contours[neigh];
        let outerTopo = topo3[neigh];

        // Update outer and inner contours with the new topo3 information needed by isPointInsidePart (but don't modify the originals) 
        outerContour = {
          ...outerContour,
          ...outerTopo
        };

        // Only include inner contours which are 1) direct children of the outer contour, and 2) neighbors of the open
        // contour. Other inner contours will not affect the outcome.
        let myInnerTopos = topo3.filter((t, idx) => t.type === 'inner' && t.parentId === outerTopo.id && neighbors[openContourIndex].includes(idx));
        let myInnerContours = myInnerTopos.map(t => {
          return {
            ...t[symContour],
            ...t
          };
        });
        // Determine whether the point is inside outerContour
        let part = {
          arcified: {
            bbox: outerContour.bbox,
            contours: [outerContour, ...myInnerContours]
          }
        };
        if ((0, _entityHitTest.isPointInsidePart)(pt, part)) {
          openTopo.childOf.push(outerTopo.id);
          break;
        }
      }
    }
  }
  if (LOG_TIMES) {
    console.log(`Assigning child contours took ${getTimeInMs(process.hrtime(start))} ms`);
    start = process.hrtime();
  }
  for (let c of contours) {
    (0, _findIntersections.removeCachedEntityBboxs)(c);
  }
  return topo3;
}
function getTimeInMs(hr) {
  return hr[0] * 1000 + hr[1] / 1e6;
}